import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import '../../styles/speechToText.css';

/**
 * SpeechToText - A reusable component for speech recognition functionality
 * 
 * This component provides a complete speech-to-text solution with support for:
 * - Text aggregation
 * - Continuous listening
 * - Interim results
 * - Multiple UI display options
 * - Method exposure via refs
 */
const SpeechToText = forwardRef(({
  onTextReceived,
  onSpeechStateChange,
  buttonClassName = 'speech-toggle',
  activeClass = 'active',
  buttonTitle = 'Toggle Speech-to-Text',
  disabled = false,
  initiallyActive = false,
  continuousListening = false,
  interimResults = true,
  language = 'en-US',
  aggregateText = false,
  resetTranscriptOnStop = false,
  showIconOnly = true
}, ref) => {
  // State for managing speech recognition
  const [isListening, setIsListening] = useState(initiallyActive);
  const [transcript, setTranscript] = useState('');
  const [aggregatedText, setAggregatedText] = useState('');
  const [browserSupported, setBrowserSupported] = useState(true);

  // Refs
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false); // Use ref to track current state in event listeners

  // Initialize speech recognition
  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.error('Speech recognition not supported in this browser');
      setBrowserSupported(false);
      return;
    }

    // Create recognition instance
    recognitionRef.current = new SpeechRecognition();
    const recognition = recognitionRef.current;
    
    // Configure recognition
    recognition.continuous = continuousListening;
    recognition.interimResults = interimResults;
    recognition.lang = language;
    
    // Set up event handlers
    recognition.onresult = (event) => {
      const result = event.results[event.results.length - 1];
      const text = result[0].transcript;
      
      // Update current transcript
      setTranscript(text);

      // If using aggregation and result is final
      if (aggregateText && result.isFinal) {
        setAggregatedText(prev => {
          const newAggregated = prev ? `${prev} ${text}` : text;
          return newAggregated;
        });
      }

      // Send to parent component if result is final or we're sending interim results
      if (result.isFinal || !interimResults) {
        // Send text to parent component
        onTextReceived && onTextReceived(text, aggregateText ? aggregatedText + ' ' + text : text);
      }
    };
    
    // Error handling
    recognition.onerror = (event) => {
      console.error('Speech Recognition Error:', event.error);
      
      // Handle "no-speech" error separately as it's common
      if (event.error !== 'no-speech') {
        setIsListening(false);
        isListeningRef.current = false;
        onSpeechStateChange && onSpeechStateChange(false);
      }
    };
    
    // Auto-restart if continuous mode is enabled
    recognition.onend = () => {
      if (isListeningRef.current && continuousListening) {
        recognition.start();
      } else {
        setIsListening(false);
        isListeningRef.current = false;
        onSpeechStateChange && onSpeechStateChange(false);
        
        // Reset transcript if option is enabled
        if (resetTranscriptOnStop) {
          setTranscript('');
        }
      }
    };

    // Start listening if initiallyActive is true
    if (initiallyActive && !disabled) {
      startListening();
    }

    // Cleanup
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        
        if (isListeningRef.current) {
          recognitionRef.current.stop();
        }
      }
    };
  }, [language, continuousListening, interimResults, initiallyActive, disabled, aggregateText]);
  
  // Toggle listening state
  const toggleListening = useCallback(() => {
    if (disabled || !browserSupported) return;
    
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, disabled, browserSupported]);
  
  // Start listening
  const startListening = useCallback(() => {
    if (!recognitionRef.current || disabled || !browserSupported) return;
    
    try {
      recognitionRef.current.start();
      setIsListening(true);
      isListeningRef.current = true;
      onSpeechStateChange && onSpeechStateChange(true);
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
    }
  }, [disabled, browserSupported, onSpeechStateChange]);
  
  // Stop listening
  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListeningRef.current) return;
    
    try {
      recognitionRef.current.stop();
      setIsListening(false);
      isListeningRef.current = false;
      onSpeechStateChange && onSpeechStateChange(false);
    } catch (error) {
      console.error('Failed to stop speech recognition:', error);
    }
  }, [onSpeechStateChange]);
  
  // Reset aggregated text
  const resetAggregatedText = useCallback(() => {
    setAggregatedText('');
  }, []);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    startListening,
    stopListening,
    resetAggregatedText,
    getTranscript: () => transcript,
    getAggregatedText: () => aggregatedText
  }));

  // Render button with icon or more complex UI based on props
  if (showIconOnly) {
    return (
      <button
        className={`${buttonClassName} ${isListening ? activeClass : ''}`}
        onClick={toggleListening}
        title={buttonTitle}
        disabled={disabled || !browserSupported}
      >
        <i className={`ri-mic-${isListening ? 'fill' : 'line'}`}></i>
      </button>
    );
  }

  // More complex UI with transcript display
  return (
    <div className="speech-to-text-container">
      <button
        className={`${buttonClassName} ${isListening ? activeClass : ''}`}
        onClick={toggleListening}
        title={buttonTitle}
        disabled={disabled || !browserSupported}
      >
        <i className={`ri-mic-${isListening ? 'fill' : 'line'}`}></i>
      </button>
      
      {transcript && (
        <div className="transcript-container">
          <p className="transcript">{transcript}</p>
        </div>
      )}
      
      {aggregateText && aggregatedText && (
        <div className="aggregated-text-container">
          <p className="aggregated-text">{aggregatedText}</p>
          <button 
            className="reset-text-button" 
            onClick={resetAggregatedText}
            title="Reset Text"
          >
            <i className="ri-refresh-line"></i>
          </button>
        </div>
      )}
      
      {!browserSupported && (
        <div className="browser-not-supported">
          Speech recognition is not supported in your browser.
        </div>
      )}
    </div>
  );
});

SpeechToText.propTypes = {
  onTextReceived: PropTypes.func, // Callback when text is recognized
  onSpeechStateChange: PropTypes.func, // Callback when listening state changes
  buttonClassName: PropTypes.string, // Custom CSS class for the button
  activeClass: PropTypes.string, // CSS class when active
  buttonTitle: PropTypes.string, // Title/tooltip for the button
  disabled: PropTypes.bool, // Whether the button is disabled
  initiallyActive: PropTypes.bool, // Whether to start listening immediately
  continuousListening: PropTypes.bool, // Whether to restart listening after results
  interimResults: PropTypes.bool, // Whether to return interim results
  language: PropTypes.string, // BCP 47 language tag
  aggregateText: PropTypes.bool, // Whether to aggregate text over time
  resetTranscriptOnStop: PropTypes.bool, // Whether to clear transcript when stopping
  showIconOnly: PropTypes.bool // If true, only shows the icon button
};

// Set display name explicitly for better debugging
SpeechToText.displayName = 'SpeechToText';

export default SpeechToText;
